import { ContractInsightsData } from '@/pages/dashboard/ContractInsights';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatCard } from '@/components/ui/stat-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { Radio, Settings, Wifi, Signal } from 'lucide-react';

interface Props {
    data: ContractInsightsData;
    onManageTrackers?: () => void;
}

export default function ContractInsightTrackers({ data, onManageTrackers }: Props) {
    const trackers = (data.vehicle_trackers || []).sort((a, b) => {
        // Sort primary first, then secondary
        if (a.type === 'primary' && b.type !== 'primary') return -1;
        if (a.type !== 'primary' && b.type === 'primary') return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    const primaryTracker = trackers.find(t => t.type === 'primary');
    const secondaryTracker = trackers.find(t => t.type === 'secondary');
    const totalTrackers = trackers.length;
    const lastUpdated = trackers.length > 0
        ? trackers.reduce((latest, t) =>
            new Date(t.created_at) > new Date(latest.created_at) ? t : latest
        ).created_at
        : null;

    return (
        <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid gap-4 md:grid-cols-4">
                <StatCard
                    title="Primary Tracker"
                    value={primaryTracker?.serial_number || 'Not Assigned'}
                    description="Main GPS tracking device assigned to this vehicle."
                />
                <StatCard
                    title="Secondary Tracker"
                    value={secondaryTracker?.serial_number || 'Not Assigned'}
                    description="Backup GPS tracking device for redundancy."
                />
                <StatCard
                    title="Total Trackers"
                    value={`${totalTrackers} Devices`}
                    description="Number of GPS tracking devices assigned to this vehicle."
                />
                <StatCard
                    title="Last Updated"
                    value={lastUpdated ? format(new Date(lastUpdated), 'MMM d, yyyy') : 'N/A'}
                    description="Most recent tracker assignment or modification date."
                />
            </div>

            {/* Trackers List */}
            <Card className="border border-border/40 bg-gradient-to-br from-background via-background to-cyan-500/5">
                <CardHeader className="flex flex-row items-center justify-between pb-4">
                    <CardTitle>
                        GPS Trackers
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="font-mono">
                            {totalTrackers} Devices
                        </Badge>
                        {onManageTrackers && (
                            <Button variant="outline" size="sm" onClick={onManageTrackers} className="h-8">
                                <Settings className="h-4 w-4 mr-1" />
                                Manage
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    {trackers.length > 0 ? (
                        <div className="grid gap-4 md:grid-cols-2">
                            {trackers.map((tracker) => (
                                <div
                                    key={tracker.id}
                                    className={`relative p-4 rounded-xl border transition-all ${tracker.type === 'primary'
                                        ? 'bg-gradient-to-br from-cyan-50/50 to-transparent border-cyan-200/50 dark:from-cyan-900/10 dark:border-cyan-800/30'
                                        : 'bg-muted/30 border-border/50'
                                        }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`h-12 w-12 rounded-xl flex items-center justify-center border ${tracker.type === 'primary'
                                            ? 'bg-muted border-border text-foreground'
                                            : 'bg-muted border-border text-muted-foreground'
                                            }`}>
                                            {tracker.type === 'primary' ? (
                                                <Signal className="h-6 w-6" />
                                            ) : (
                                                <Wifi className="h-6 w-6" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Badge
                                                    variant={tracker.type === 'primary' ? 'default' : 'secondary'}
                                                    className="text-[10px] h-5 px-1.5 capitalize"
                                                >
                                                    {tracker.type}
                                                </Badge>
                                            </div>
                                            <p className="font-mono font-semibold text-lg truncate">
                                                {tracker.serial_number || 'Unknown'}
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                {tracker.sim_number ? `SIM: ${tracker.sim_number}` : 'No SIM assigned'}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-2">
                                                Added {format(new Date(tracker.created_at), 'MMM d, yyyy')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-xl bg-muted/20">
                            <Radio className="h-8 w-8 mx-auto mb-3 opacity-30" />
                            <p className="text-sm font-medium">No trackers assigned</p>
                            <p className="text-xs text-muted-foreground mt-1">Assign GPS trackers to monitor this vehicle's location</p>
                            {onManageTrackers && (
                                <Button variant="outline" size="sm" onClick={onManageTrackers} className="mt-4">
                                    <Settings className="h-4 w-4 mr-1" />
                                    Manage Trackers
                                </Button>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
